import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exportFilename } from "@/lib/timefmt";
import { toast } from "sonner";
import { Download, Archive, Upload, RotateCcw } from "lucide-react";
import { SortTh, FilterTh, filterRows, sortRows } from "@/components/tableTools";

export default function BackupTab() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [allCompanies, setAllCompanies] = useState(false);
  const [allData, setAllData] = useState(false);
  const [snaps, setSnaps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [qf, setQf] = useState({ date: "", company: "", size: "" });
  const fileRef = useRef(null);

  const [restoreData, setRestoreData] = useState(null);
  const [restoreName, setRestoreName] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [diffSort, setDiffSort] = useState({ key: "collection", dir: "asc" });
  const [diffQf, setDiffQf] = useState({ collection: "", company: "" });
  const [logsDownloaded, setLogsDownloaded] = useState(false);
  const [clearingLogs, setClearingLogs] = useState(false);

  const downloadLogs = async () => {
    try {
      const params = allCompanies ? {} : (companyId ? { company_id: companyId } : {});
      const res = await api.get("/admin/logs/export", { params, responseType: "blob" });
      saveBlob(res.data, exportFilename("logs-backup", "json", user));
      setLogsDownloaded(true);
      toast.success("Log files downloaded — you can now clear old entries");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clearLogs = async () => {
    if (!logsDownloaded) { toast.error("Download a copy of the logs first"); return; }
    if (!window.confirm("Delete ALL security and export log entries older than 7 days? This cannot be undone.")) return;
    setClearingLogs(true);
    try {
      const params = allCompanies ? {} : { company_id: companyId };
      const { data } = await api.post("/admin/logs/clear", params);
      toast.success(`Cleared ${data.deleted.security_logs + data.deleted.export_logs} old log entries (kept last 7 days)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setClearingLogs(false); }
  };

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSnaps = () => {
    const params = allCompanies ? {} : (companyId ? { company_id: companyId } : {});
    api.get("/admin/backups", { params }).then((r) => setSnaps(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };
  useEffect(() => { loadSnaps(); }, [companyId, allCompanies]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBlob = (data, filename) => {
    const url = URL.createObjectURL(new Blob([data], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCurrent = async () => {
    if (allData) {
      setBusy(true);
      try {
        const res = await api.get("/admin/backup/full", { responseType: "blob" });
        saveBlob(res.data, exportFilename("full-backup", "json", user));
        toast.success("Full data backup downloaded");
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setBusy(false); }
      return;
    }
    if (!allCompanies && !companyId) { toast.error("Pick a company"); return; }
    setBusy(true);
    try {
      const params = allCompanies ? { all_companies: true } : { company_id: companyId };
      const res = await api.get("/admin/backup/config", { params, responseType: "blob" });
      saveBlob(res.data, exportFilename(allCompanies ? "config-backup-all" : "config-backup", "json", user));
      toast.success("Configuration backup downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadSnap = async (s) => {
    try {
      const res = await api.get(`/admin/backups/${s.id}/download`, { responseType: "blob" });
      saveBlob(res.data, `config-backup-${(s.company_name || "company").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${s.date}.json`);
      toast.success(`Snapshot from ${s.date} downloaded`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadSnapshotForRestore = async (id) => {
    if (!id) return;
    try {
      const res = await api.get(`/admin/backups/${id}/download`);
      setRestoreData(res.data);
      setRestoreName(`snapshot ${id.slice(0, 8)}…`);
      setPreview(null);
      setConfirmText("");
      toast.success("Snapshot loaded — run a preview to see the diff");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      try {
        setRestoreData(JSON.parse(rd.result));
        setRestoreName(f.name);
        setPreview(null);
        setConfirmText("");
        toast.success(`Loaded ${f.name} — run a preview to see the diff`);
      } catch { toast.error("Not a valid JSON file"); }
    };
    rd.readAsText(f);
    e.target.value = "";
  };

  const doPreview = async () => {
    if (!restoreData) { toast.error("Choose a backup file or snapshot first"); return; }
    try {
      const { data } = await api.post("/admin/restore/preview", { data: restoreData });
      setPreview(data);
      toast.success("Dry run complete — nothing was changed");
    } catch (e) {
      setPreview(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doRestore = async () => {
    if (confirmText !== "RESTORE") { toast.error('Type RESTORE to confirm'); return; }
    setRestoring(true);
    try {
      const { data } = await api.post("/admin/restore/execute", { data: restoreData, confirm: confirmText });
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restore complete — ${total} documents restored`);
      setPreview(null);
      setRestoreData(null);
      setRestoreName("");
      setConfirmText("");
      loadSnaps();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRestoring(false); }
  };

  const snapText = (s) => ({ date: `${s.date} ${s.created_at || ""}`, company: s.company_name || "", size: s.size });
  const snapKey = (s, k) => (k === "size" ? s.size || 0 : k === "date" ? s.created_at || s.date || "" : s.company_name || "");
  const shown = sortRows(filterRows(snaps, snapText, qf), sort, snapKey);

  const diffText = (r) => ({ collection: r.collection, company: r.company || "" });
  const diffKey = (r, k) => (["existing", "incoming", "added", "updated", "removed"].includes(k) ? (r[k] ?? -1) : diffText(r)[k] || "");
  const shownDiff = sortRows(filterRows(preview?.diff || [], diffText, diffQf), diffSort, diffKey);
  const isFull = restoreData?.kind === "crew-ready-full-backup";

  return (
    <div className="space-y-5">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 max-w-xl space-y-4" data-testid="backup-card">
        <div>
          <div className="font-heading text-lg font-semibold">Configuration Backup</div>
          <p className="text-xs text-slate-500 mt-1">
            Downloads all configuration as a JSON file: companies, roles, question options, organisation,
            duty templates &amp; patterns, activity config (types, modes, sequences), equipment, locations and
            application settings. Member data and fatigue entries are not included.
          </p>
        </div>
        {!allCompanies && !allData && (
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="backup-company-select"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
        {isSuper && (
          <>
            <label className="flex items-center gap-2.5 text-sm">
              <input type="checkbox" checked={allCompanies} onChange={(e) => setAllCompanies(e.target.checked)} disabled={allData}
                data-testid="backup-all-companies" className="accent-blue-600 h-4 w-4" />
              All companies (super/global admin)
            </label>
            <label className="flex items-center gap-2.5 text-sm">
              <input type="checkbox" checked={allData} onChange={(e) => setAllData(e.target.checked)}
                data-testid="backup-all-data" className="accent-blue-600 h-4 w-4" />
              <span>Back up <strong>ALL data</strong> — everything above <em>plus</em> members, fatigue entries, tasks, commitments and logs (super/global admin)</span>
            </label>
          </>
        )}
        <button onClick={downloadCurrent} disabled={busy} data-testid="backup-download-btn"
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60">
          <Download className="h-4 w-4" /> {busy ? "Preparing…" : allData ? "Download FULL data backup" : "Download configuration file"}
        </button>
      </div>

      <div className="p-5 rounded-lg border border-amber-600/30 bg-slate-900/60 space-y-4" data-testid="restore-card">
        <div>
          <div className="font-heading text-lg font-semibold flex items-center gap-2"><RotateCcw className="h-4 w-4 text-amber-400" /> Restore from backup</div>
          <p className="text-xs text-slate-500 mt-1">
            Upload a backup file (or load a stored snapshot), run a dry-run preview to see exactly what would change,
            then confirm. Config restores replace the company&apos;s configuration; full backups replace ALL data.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={fileRef} type="file" accept=".json,application/json" onChange={pickFile} className="hidden" data-testid="restore-file-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="restore-choose-file"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs">
            <Upload className="h-3.5 w-3.5" /> Choose backup file…
          </button>
          {snaps.length > 0 && (
            <select defaultValue="" onChange={(e) => { loadSnapshotForRestore(e.target.value); e.target.value = ""; }} data-testid="restore-snapshot-select"
              className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-xs">
              <option value="" disabled>…or load a stored snapshot</option>
              {snaps.map((s) => <option key={s.id} value={s.id} label={`${s.company_name} · ${s.date}`} />)}
            </select>
          )}
          {restoreName && (
            <span className="text-xs text-slate-400 font-mono-num" data-testid="restore-loaded-name">
              {restoreName} {isFull ? "· FULL DATA backup" : "· config backup"}
            </span>
          )}
          <button onClick={doPreview} disabled={!restoreData} data-testid="restore-preview-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-600/40 text-xs font-medium disabled:opacity-40">
            Preview changes (dry run)
          </button>
        </div>

        {preview && (
          <div className="space-y-3" data-testid="restore-preview">
            <div className="text-xs text-slate-400">
              Backup from <span className="font-mono-num text-slate-300">{preview.generated_at || "unknown"}</span> by {preview.generated_by || "unknown"}
            </div>
            {preview.warnings?.map((w) => (
              <div key={w} className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-3 py-2" data-testid="restore-warning">{w}</div>
            ))}
            <div className="rounded-md border border-slate-800 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left">
                    <SortTh label="Collection" k="collection" sort={diffSort} setSort={setDiffSort} />
                    <SortTh label="Company" k="company" sort={diffSort} setSort={setDiffSort} />
                    <SortTh label="Current" k="existing" sort={diffSort} setSort={setDiffSort} />
                    <SortTh label="In file" k="incoming" sort={diffSort} setSort={setDiffSort} />
                    <SortTh label="+ Add" k="added" sort={diffSort} setSort={setDiffSort} />
                    <SortTh label="~ Change" k="updated" sort={diffSort} setSort={setDiffSort} />
                    <SortTh label="− Remove" k="removed" sort={diffSort} setSort={setDiffSort} />
                  </tr>
                  <tr className="border-b border-slate-800 text-left">
                    <FilterTh value={diffQf.collection} onChange={(v) => setDiffQf((f) => ({ ...f, collection: v }))} testId="restore-diff-filter-collection" />
                    <FilterTh value={diffQf.company} onChange={(v) => setDiffQf((f) => ({ ...f, company: v }))} testId="restore-diff-filter-company" />
                    <th colSpan={5} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {shownDiff.map((r, i) => (
                    <tr key={`${r.company}-${r.collection}-${i}`} className="hover:bg-slate-900/60" data-testid={`restore-diff-row-${r.collection}`}>
                      <td className="px-4 py-2 font-mono-num text-xs text-slate-200">{r.collection}</td>
                      <td className="px-4 py-2 text-xs text-slate-400">{r.company || "—"}</td>
                      <td className="px-4 py-2 font-mono-num text-xs text-slate-400">{r.existing}</td>
                      <td className="px-4 py-2 font-mono-num text-xs text-slate-400">{r.incoming}</td>
                      <td className={`px-4 py-2 font-mono-num text-xs ${r.added ? "text-emerald-400" : "text-slate-600"}`}>{r.added}</td>
                      <td className={`px-4 py-2 font-mono-num text-xs ${r.updated ? "text-amber-400" : "text-slate-600"}`}>{r.updated ?? "—"}</td>
                      <td className={`px-4 py-2 font-mono-num text-xs ${r.removed ? "text-rose-400" : "text-slate-600"}`}>{r.removed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder='Type RESTORE to confirm'
                data-testid="restore-confirm-input"
                className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm w-56" />
              <button onClick={doRestore} disabled={restoring || confirmText !== "RESTORE"} data-testid="restore-execute-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-600/40 text-xs font-semibold disabled:opacity-40">
                <RotateCcw className="h-3.5 w-3.5" /> {restoring ? "Restoring…" : isFull ? "Restore ALL data" : "Restore configuration"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 max-w-xl space-y-4" data-testid="logs-card">
        <div>
          <div className="font-heading text-lg font-semibold">Log files</div>
          <p className="text-xs text-slate-500 mt-1">
            Download a full copy of the Security Log and Export Log as JSON, then clear old entries.
            Clearing removes everything except the last 7 days.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={downloadLogs} data-testid="logs-export-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs">
            <Download className="h-3.5 w-3.5" /> 1. Download logs copy
          </button>
          <button onClick={clearLogs} disabled={!logsDownloaded || clearingLogs} data-testid="logs-clear-btn"
            title={logsDownloaded ? "Delete log entries older than 7 days" : "Download a copy first"}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-600/40 text-xs font-semibold disabled:opacity-40">
            <RotateCcw className="h-3.5 w-3.5" /> {clearingLogs ? "Clearing…" : "2. Clear logs older than 7 days"}
          </button>
          {logsDownloaded && <span className="text-[11px] text-emerald-400" data-testid="logs-downloaded-flag">Copy downloaded</span>}
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid="backup-snapshots">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
          <Archive className="h-4 w-4 text-slate-400" />
          <div className="font-heading font-semibold text-sm">Stored daily snapshots</div>
          <span className="text-[11px] text-slate-500">— a snapshot per company is stored automatically every day (last 30 kept)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <SortTh label="Date" k="date" sort={sort} setSort={setSort} />
                <SortTh label="Company" k="company" sort={sort} setSort={setSort} />
                <SortTh label="Size" k="size" sort={sort} setSort={setSort} />
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-widest text-slate-400 font-mono-num font-medium text-right">Download</th>
              </tr>
              <tr className="border-b border-slate-800 text-left">
                <FilterTh value={qf.date} onChange={(v) => setQf((f) => ({ ...f, date: v }))} testId="backup-qfilter-date" />
                <FilterTh value={qf.company} onChange={(v) => setQf((f) => ({ ...f, company: v }))} testId="backup-qfilter-company" />
                <FilterTh value={qf.size} onChange={(v) => setQf((f) => ({ ...f, size: v }))} testId="backup-qfilter-size" />
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {shown.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No snapshots stored yet — the daily job runs at 05:30 UTC.</td></tr>
              )}
              {shown.map((s) => (
                <tr key={s.id} className="hover:bg-slate-900/60" data-testid={`backup-row-${s.id}`}>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-300">{s.date}</td>
                  <td className="px-4 py-3 text-sm">{s.company_name}
                    {s.origin === "pre-restore" && <span className="ml-2 text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/40 rounded px-1 py-0.5">auto pre-restore</span>}
                  </td>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-400">{((s.size || 0) / 1024).toFixed(1)} KB</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => downloadSnap(s)} data-testid={`backup-download-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs">
                      <Download className="h-3 w-3" /> JSON
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
